import type {
  ChatJob,
  ChatJobAccepted,
  CognitiveGraph,
  LearningJob,
  MemoryRecord,
  SuggestedMemoryRecord
} from "./types";

/**
 * Todo acesso a API passa pelo /gateway do proprio host. O navegador nunca fala com a ARTI.Api
 * diretamente — e o que mantem uma origem so, sem CORS e sem a URL da API vazando para o cliente.
 */
const GATEWAY = "/gateway";

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${GATEWAY}${path}`, {
    cache: "no-store",
    ...init,
    headers: init?.body ? { "Content-Type": "application/json", ...init?.headers } : init?.headers
  });

  if (!response.ok) {
    throw new ApiError(await describeFailure(response), response.status);
  }

  return (await response.json()) as T;
}

/**
 * Para respostas sem corpo. `reject` devolve 204, e tentar interpretar um corpo vazio como JSON
 * transformaria sucesso em erro.
 */
async function send(path: string, init: RequestInit): Promise<void> {
  const response = await fetch(`${GATEWAY}${path}`, { cache: "no-store", ...init });
  if (!response.ok) {
    throw new ApiError(await describeFailure(response), response.status);
  }
}

async function describeFailure(response: Response): Promise<string> {
  try {
    const problem = (await response.json()) as { title?: string; detail?: string };
    return problem.detail ?? problem.title ?? `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

export interface SendMessageInput {
  userId: string;
  conversationId: string | null;
  message: string;
  responseMode: string;
  jobId: string;
}

export const api = {
  /**
   * O chat e assincrono de proposito: com modelo local um turno pode levar dezenas de segundos, e
   * o job sobrevive a aba fechada. O jobId vai do cliente para que reenviar seja idempotente.
   */
  enqueueMessage(input: SendMessageInput): Promise<ChatJobAccepted> {
    return request<ChatJobAccepted>("/api/v1/chat/jobs", {
      method: "POST",
      body: JSON.stringify(input)
    });
  },

  getChatJob(jobId: string, userId: string): Promise<ChatJob> {
    return request<ChatJob>(`/api/v1/chat/jobs/${jobId}?userId=${encodeURIComponent(userId)}`);
  },

  getLearningJob(jobId: string, userId: string): Promise<LearningJob> {
    return request<LearningJob>(`/api/v1/learning-jobs/${jobId}?userId=${encodeURIComponent(userId)}`);
  },

  getCognitiveGraph(params: {
    userId: string;
    focus?: string | null;
    depth?: number;
    limit?: number;
  }): Promise<CognitiveGraph> {
    const query = new URLSearchParams({ userId: params.userId });
    if (params.focus) {
      query.set("focus", params.focus);
    }
    if (params.depth) {
      query.set("depth", String(params.depth));
    }
    if (params.limit) {
      query.set("limit", String(params.limit));
    }
    return request<CognitiveGraph>(`/api/v1/cognitive-graph?${query.toString()}`);
  },

  listPendingSuggestions(userId: string): Promise<SuggestedMemoryRecord[]> {
    return request<SuggestedMemoryRecord[]>(
      `/api/v1/suggested-memories?userId=${encodeURIComponent(userId)}&status=Pending`
    );
  },

  /**
   * Aprovar promove a sugestao a memoria e e o unico caminho pelo qual um no nasce no Grafo
   * Cognitivo. Rejeitar descarta sem gravar nada.
   */
  approveSuggestion(suggestionId: string): Promise<void> {
    return send(`/api/v1/suggested-memories/${suggestionId}/approve`, { method: "POST" });
  },

  rejectSuggestion(suggestionId: string): Promise<void> {
    return send(`/api/v1/suggested-memories/${suggestionId}/reject`, { method: "POST" });
  },

  getMemory(memoryId: string): Promise<MemoryRecord> {
    return request<MemoryRecord>(`/api/v1/memories/${memoryId}`);
  },

  async isHealthy(): Promise<boolean> {
    try {
      const response = await fetch(`${GATEWAY}/health/live`, { cache: "no-store" });
      return response.ok;
    } catch {
      return false;
    }
  }
};
